import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { getUserFriendlyError } from "@/lib/error-handler";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Users,
  Search,
  Eye,
  Edit,
  ShieldBan,
  ShieldCheck,
  MessageSquare,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Link } from "react-router-dom";

const PROFILE_TYPES = [
  { value: "banda", label: "Banda" },
  { value: "artista", label: "Artista" },
  { value: "promotora", label: "Promotora" },
  { value: "manager", label: "Manager" },
  { value: "tecnico", label: "Técnico/a" },
  { value: "otro", label: "Otro" },
];

const PAGE_SIZE = 20;

type ReviewCounts = {
  total: number;
  approved: number;
  pending: number;
  rejected: number;
};

export default function AdminUsers() {
  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterActivity, setFilterActivity] = useState("all");
  const [filterDate, setFilterDate] = useState("all");
  const [filterNewsletter, setFilterNewsletter] = useState("all");
  const [page, setPage] = useState(0);
  const [blockDialog, setBlockDialog] = useState<{ id: string; action: "block" | "unblock" } | null>(null);
  const [blockReason, setBlockReason] = useState("");

  // Fetch all profiles
  const { data: profiles, isLoading } = useQuery({
    queryKey: ["admin-users"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  // Fetch review counts per user
  const { data: reviewCounts } = useQuery({
    queryKey: ["admin-users-review-counts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("reviews")
        .select("user_id, status");
      if (error) throw error;

      const counts: Record<string, ReviewCounts> = {};
      for (const r of data) {
        if (!counts[r.user_id]) counts[r.user_id] = { total: 0, approved: 0, pending: 0, rejected: 0 };
        counts[r.user_id].total++;
        if (r.status === "approved") counts[r.user_id].approved++;
        else if (r.status === "pending") counts[r.user_id].pending++;
        else if (r.status === "rejected") counts[r.user_id].rejected++;
      }
      return counts;
    },
  });

  const getCounts = (userId: string): ReviewCounts =>
    reviewCounts?.[userId] ?? { total: 0, approved: 0, pending: 0, rejected: 0 };

  // Filter + search
  const filtered = useMemo(() => {
    if (!profiles) return [];
    const now = new Date();
    return profiles.filter((p) => {
      // Search
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const match =
          (p.visible_name ?? "").toLowerCase().includes(term) ||
          (p.email ?? "").toLowerCase().includes(term) ||
          (p.city ?? "").toLowerCase().includes(term);
        if (!match) return false;
      }
      // Type
      if (filterType !== "all" && p.profile_type !== filterType) return false;
      // Status
      const status = (p as any).account_status ?? "active";
      if (filterStatus !== "all" && status !== filterStatus) return false;
      // Activity
      if (filterActivity !== "all") {
        const c = getCounts(p.id);
        if (filterActivity === "with_reviews" && c.total === 0) return false;
        if (filterActivity === "no_reviews" && c.total > 0) return false;
        if (filterActivity === "pending" && c.pending === 0) return false;
        if (filterActivity === "rejected" && c.rejected === 0) return false;
      }
      // Date
      if (filterDate !== "all") {
        const created = new Date(p.created_at);
        const diffDays = (now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24);
        if (filterDate === "7d" && diffDays > 7) return false;
        if (filterDate === "30d" && diffDays > 30) return false;
      }
      // Newsletter
      if (filterNewsletter !== "all") {
        const optIn = (p as any).newsletter_opt_in ?? false;
        if (filterNewsletter === "yes" && !optIn) return false;
        if (filterNewsletter === "no" && optIn) return false;
      }
      return true;
    });
  }, [profiles, searchTerm, filterType, filterStatus, filterActivity, filterDate, filterNewsletter, reviewCounts]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // Block/Unblock mutation
  const statusMutation = useMutation({
    mutationFn: async ({ id, action, reason }: { id: string; action: "block" | "unblock"; reason: string }) => {
      const newStatus = action === "block" ? "blocked" : "active";
      const oldProfile = profiles?.find((p) => p.id === id);
      const oldStatus = (oldProfile as any)?.account_status ?? "active";

      const { error: updateError } = await supabase
        .from("profiles")
        .update({ account_status: newStatus } as any)
        .eq("id", id);
      if (updateError) throw updateError;

      const { error: logError } = await supabase.from("account_status_log" as any).insert({
        user_id: id,
        old_status: oldStatus,
        new_status: newStatus,
        reason: reason || null,
        changed_by: user!.id,
      });
      if (logError) throw logError;
    },
    onSuccess: (_, { action }) => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      toast({ title: action === "block" ? "Usuario bloqueado" : "Usuario desbloqueado" });
      setBlockDialog(null);
      setBlockReason("");
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: getUserFriendlyError(err), variant: "destructive" });
    },
  });

  const profileTypeLabel = (type: string | null) =>
    PROFILE_TYPES.find((pt) => pt.value === type)?.label ?? type ?? "—";

  return (
    <AdminLayout requiredModule="usuarios_comunidad" requiredPermission="can_view">
      <div>
        <div className="mb-6">
          <h1 className="font-display text-2xl font-bold flex items-center gap-2">
            <Users className="h-6 w-6" /> Gestión de Usuarios
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {filtered.length} usuario{filtered.length !== 1 ? "s" : ""}
          </p>
        </div>

        {/* Search & Filters */}
        <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
          <div className="relative sm:col-span-2 lg:col-span-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Buscar nombre, email, ciudad…"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
              className="pl-9"
            />
          </div>
          <Select value={filterType} onValueChange={(v) => { setFilterType(v); setPage(0); }}>
            <SelectTrigger><SelectValue placeholder="Tipo de perfil" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los tipos</SelectItem>
              {PROFILE_TYPES.map((pt) => (
                <SelectItem key={pt.value} value={pt.value}>{pt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={(v) => { setFilterStatus(v); setPage(0); }}>
            <SelectTrigger><SelectValue placeholder="Estado" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estados</SelectItem>
              <SelectItem value="active">Activa</SelectItem>
              <SelectItem value="blocked">Bloqueada</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterActivity} onValueChange={(v) => { setFilterActivity(v); setPage(0); }}>
            <SelectTrigger><SelectValue placeholder="Actividad" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toda actividad</SelectItem>
              <SelectItem value="with_reviews">Con reviews</SelectItem>
              <SelectItem value="no_reviews">Sin reviews</SelectItem>
              <SelectItem value="pending">Con pendientes</SelectItem>
              <SelectItem value="rejected">Con rechazadas</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterDate} onValueChange={(v) => { setFilterDate(v); setPage(0); }}>
            <SelectTrigger><SelectValue placeholder="Fecha alta" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Cualquier fecha</SelectItem>
              <SelectItem value="7d">Últimos 7 días</SelectItem>
              <SelectItem value="30d">Últimos 30 días</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterNewsletter} onValueChange={(v) => { setFilterNewsletter(v); setPage(0); }}>
            <SelectTrigger><SelectValue placeholder="Newsletter" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Newsletter: Todos</SelectItem>
              <SelectItem value="yes">Newsletter: Sí</SelectItem>
              <SelectItem value="no">Newsletter: No</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-12" />)}
          </div>
        ) : !paginated.length ? (
          <div className="py-16 text-center text-muted-foreground">
            <Users className="mx-auto mb-2 h-8 w-8 opacity-40" />
            <p>No se encontraron usuarios</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre visible</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Ciudad</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-center">Reviews</TableHead>
                    <TableHead className="text-center">Aprob.</TableHead>
                     <TableHead className="text-center">Pend.</TableHead>
                     <TableHead className="text-center">Newsletter</TableHead>
                     <TableHead>Registro</TableHead>
                     <TableHead>Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginated.map((p) => {
                    const c = getCounts(p.id);
                    const status = (p as any).account_status ?? "active";
                    const isBlocked = status === "blocked";

                    return (
                      <TableRow key={p.id} className={isBlocked ? "opacity-60" : ""}>
                        <TableCell className="font-medium">
                          {p.visible_name || p.username || "—"}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {profileTypeLabel(p.profile_type)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {p.email ?? "—"}
                        </TableCell>
                        <TableCell className="text-sm">{p.city ?? "—"}</TableCell>
                        <TableCell>
                          <Badge className={isBlocked ? "bg-destructive/10 text-destructive" : "bg-success/10 text-success"}>
                            {isBlocked ? "Bloqueada" : "Activa"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">{c.total}</TableCell>
                        <TableCell className="text-center">{c.approved}</TableCell>
                        <TableCell className="text-center">{c.pending}</TableCell>
                        <TableCell className="text-center">
                          {(p as any).newsletter_opt_in ? (
                            <Badge className="bg-success/10 text-success text-xs">Sí</Badge>
                          ) : (
                            <span className="text-xs text-muted-foreground">No</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {new Date(p.created_at).toLocaleDateString("es-ES")}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button asChild variant="ghost" size="icon" title="Ver detalle">
                              <Link to={`/admin/users/${p.id}`}>
                                <Eye className="h-4 w-4" />
                              </Link>
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              title={isBlocked ? "Desbloquear" : "Bloquear"}
                              onClick={() => setBlockDialog({ id: p.id, action: isBlocked ? "unblock" : "block" })}
                            >
                              {isBlocked ? (
                                <ShieldCheck className="h-4 w-4 text-success" />
                              ) : (
                                <ShieldBan className="h-4 w-4 text-destructive" />
                              )}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-4 flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  Página {page + 1} de {totalPages}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 0}
                    onClick={() => setPage(page - 1)}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= totalPages - 1}
                    onClick={() => setPage(page + 1)}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Block/Unblock Dialog */}
        <Dialog open={!!blockDialog} onOpenChange={(o) => { if (!o) { setBlockDialog(null); setBlockReason(""); } }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {blockDialog?.action === "block" ? "Bloquear usuario" : "Desbloquear usuario"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {blockDialog?.action === "block" && (
                <div>
                  <p className="text-sm text-muted-foreground mb-2">
                    El usuario no podrá iniciar sesión ni crear/editar reviews.
                  </p>
                  <Textarea
                    placeholder="Motivo del bloqueo (opcional)"
                    value={blockReason}
                    onChange={(e) => setBlockReason(e.target.value)}
                  />
                </div>
              )}
              {blockDialog?.action === "unblock" && (
                <p className="text-sm text-muted-foreground">
                  ¿Estás seguro de que quieres reactivar esta cuenta?
                </p>
              )}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => { setBlockDialog(null); setBlockReason(""); }}>
                  Cancelar
                </Button>
                <Button
                  variant={blockDialog?.action === "block" ? "destructive" : "default"}
                  onClick={() => {
                    if (blockDialog) {
                      statusMutation.mutate({
                        id: blockDialog.id,
                        action: blockDialog.action,
                        reason: blockReason,
                      });
                    }
                  }}
                  disabled={statusMutation.isPending}
                >
                  {blockDialog?.action === "block" ? "Bloquear" : "Desbloquear"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
